import React, { useEffect, useRef, useState } from 'react';
import { Send, User } from 'lucide-react';
import { db } from '../lib/firebase.js';
import { onSnapshot, collection, addDoc, query as fsQuery, limit, doc, setDoc } from 'firebase/firestore';
import { cleanPhone } from '../lib/compat.js';

function initials(name) {
  if (!name) return '?';
  return name.split(' ').map((w) => w[0] || '').join('').slice(0, 2).toUpperCase();
}

export default function AdminChat({ data }) {
  const [conversations, setConversations] = useState([]);
  const [searchQ, setSearchQ] = useState('');
  const [activePhone, setActivePhone] = useState(null);
  const [messages, setMessages] = useState([]);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [msgUnsub, setMsgUnsub] = useState(null); // stored as { fn: unsubFn }
  const messagesEndRef = useRef(null);

  // Load conversations from chats collection
  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'chats'), (snap) => {
      const convs = snap.docs.map((d) => {
        const chatData = d.data();
        return {
          phone: d.id,
          name: chatData.name || d.id,
          lastMsg: chatData.lastMsg || chatData.lastMessage || '',
          lastTime: chatData.lastMsgAt || chatData.updatedAt || '',
          unreadAdmin: chatData.unreadAdmin || 0
        };
      }).sort((a, b) => String(b.lastTime || '').localeCompare(String(a.lastTime || '')));
      setConversations(convs);
    }, (err) => console.error('Chat conv error:', err));
    return () => unsub();
  }, []);

  // Load messages for active conversation
  useEffect(() => {
    if (msgUnsub?.fn) { msgUnsub.fn(); setMsgUnsub(null); }
    if (!activePhone) { setMessages([]); return; }
    // Use limit only, sort client-side to avoid composite index requirement
    const q = fsQuery(collection(db, 'chats', activePhone, 'messages'), limit(150));
    const unsub = onSnapshot(q, (snap) => {
      const msgs = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => String(a.createdAt || '').localeCompare(String(b.createdAt || '')));
      setMessages(msgs);
      setDoc(doc(db, 'chats', activePhone), { unreadAdmin: 0 }, { merge: true }).catch(() => {});
    });
    // Store unsub ref using a wrapper to avoid React treating it as a state updater fn
    setMsgUnsub({ fn: unsub });
    return () => unsub();
  }, [activePhone]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  async function sendReply() {
    if (!replyText.trim() || !activePhone || sending) return;
    setSending(true);
    const text = replyText.trim();
    setReplyText('');
    try {
      await addDoc(collection(db, 'chats', activePhone, 'messages'), {
        text,
        sender: 'admin',
        createdAt: new Date().toISOString(),
        read: true
      });
      await setDoc(doc(db, 'chats', activePhone), {
        lastMsg: text,
        lastMsgAt: new Date().toISOString(),
        unreadUser: 1
      }, { merge: true });
    } catch (e) {
      console.error('Send reply error:', e);
    } finally {
      setSending(false);
    }
  }

  function onKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); }
  }

  const filteredConvs = searchQ ? conversations.filter((c) => c.name.toLowerCase().includes(searchQ.toLowerCase()) || c.phone.includes(searchQ)) : conversations;
  const activeConv = conversations.find((c) => c.phone === activePhone);

  // Find student info for active phone
  const studentInfo = activePhone ? data.students.find((s) => cleanPhone(s.phone || s.id) === activePhone) : null;
  const totalUnreadCount = conversations.reduce((s, c) => s + Number(c.unreadAdmin || 0), 0);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', height: 'calc(100vh - 120px)', background: '#fff', borderRadius: 14, border: '1px solid var(--admin-border)', overflow: 'hidden' }}>
      {/* Left: Conversation list */}
      <div style={{ borderRight: '1px solid var(--admin-border)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--admin-border)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--admin-navy)' }}>
                Conversations
                {totalUnreadCount > 0 && <span style={{ background: '#EA4335', color: '#fff', fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 100, marginLeft: 8 }}>{totalUnreadCount}</span>}
              </div>
              <div style={{ fontSize: 11, color: 'var(--admin-muted)' }}>{filteredConvs.length} conversation(s)</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, background: '#E8F5EE', color: '#1a7a3e', fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 100 }}>
              <span style={{ width: 5, height: 5, background: '#1a7a3e', borderRadius: '50%', display: 'inline-block' }} />
              Live
            </div>
          </div>
          <input
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            placeholder="Search by name or phone…"
            style={{ marginTop: 8, padding: '7px 10px', border: '1.5px solid var(--admin-border)', borderRadius: 8, fontSize: 12, outline: 'none', width: '100%' }}
          />
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {filteredConvs.length ? filteredConvs.map((conv) => (
            <div
              key={conv.phone}
              onClick={() => setActivePhone(conv.phone)}
              style={{
                padding: '12px 14px',
                borderBottom: '1px solid var(--admin-border)',
                cursor: 'pointer',
                background: activePhone === conv.phone ? 'var(--cream-dk)' : 'transparent',
                borderLeft: activePhone === conv.phone ? '3px solid var(--admin-navy)' : '3px solid transparent',
                position: 'relative'
              }}
            >
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--admin-navy)', color: '#F8D72F', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
                  {initials(conv.name)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <strong style={{ fontSize: 13, color: 'var(--admin-navy)' }}>{conv.name}</strong>
                    {conv.unreadAdmin > 0 && <span style={{ background: '#EA4335', color: '#fff', fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 100 }}>{conv.unreadAdmin}</span>}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--admin-muted)' }}>{conv.phone}</div>
                  <div style={{ fontSize: 11, color: 'var(--admin-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>{conv.lastMsg || 'No messages'}</div>
                </div>
              </div>
            </div>
          )) : <div style={{ padding: '24px 16px', textAlign: 'center', color: 'var(--admin-muted)', fontSize: 13 }}>No conversations</div>}
        </div>
      </div>

      {/* Right: Thread */}
      <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {!activePhone ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'var(--admin-muted)', gap: 10 }}>
            <div style={{ fontSize: 48 }}>💬</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--admin-navy)' }}>Select a conversation</div>
            <div style={{ fontSize: 13 }}>Click any chat on the left to open it</div>
          </div>
        ) : (
          <>
            {/* Thread header */}
            <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--admin-border)', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0, background: '#fff' }}>
              <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'var(--admin-navy)', color: '#F8D72F', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700 }}>
                {initials(studentInfo?.name || activeConv?.name)}
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--admin-navy)' }}>{studentInfo?.name || activeConv?.name || activePhone}</div>
                <div style={{ fontSize: 11, color: 'var(--admin-muted)' }}>{activePhone}{studentInfo?.district ? ` — ${studentInfo.district}` : ''}</div>
              </div>
              {studentInfo && (
                <div style={{ marginLeft: 'auto', background: 'var(--cream-dk)', border: 'none', padding: '6px 12px', borderRadius: 7, fontSize: 11, fontWeight: 600, color: 'var(--admin-navy)', display: 'flex', alignItems: 'center', gap: 5 }}>
                  <User size={13} />
                  {studentInfo.district || 'Student'}
                </div>
              )}
            </div>

            {/* Messages */}
            <div style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 10, background: '#F8F8FF' }}>
              {messages.length ? messages.map((msg) => (
                <div key={msg.id} style={{ display: 'flex', justifyContent: msg.sender === 'admin' ? 'flex-end' : 'flex-start' }}>
                  <div style={{
                    maxWidth: '72%',
                    background: msg.sender === 'admin' ? 'var(--admin-navy)' : '#fff',
                    color: msg.sender === 'admin' ? '#fff' : 'var(--admin-text)',
                    borderRadius: msg.sender === 'admin' ? '12px 12px 3px 12px' : '12px 12px 12px 3px',
                    padding: '9px 13px',
                    fontSize: 13,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                    border: msg.sender !== 'admin' ? '1px solid var(--admin-border)' : 'none'
                  }}>
                    <div style={{ lineHeight: 1.5 }}>{msg.text}</div>
                    <div style={{ fontSize: 10, opacity: 0.6, marginTop: 3, textAlign: 'right' }}>
                      {msg.createdAt ? new Date(msg.createdAt instanceof Object && msg.createdAt.toDate ? msg.createdAt.toDate() : msg.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : ''}
                    </div>
                  </div>
                </div>
              )) : <div style={{ textAlign: 'center', color: 'var(--admin-muted)', fontSize: 13, marginTop: 40 }}>No messages yet</div>}
              <div ref={messagesEndRef} />
            </div>

            {/* Reply box */}
            <div style={{ padding: '10px 14px', borderTop: '1px solid var(--admin-border)', display: 'flex', gap: 10, background: '#fff', flexShrink: 0 }}>
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Type your reply… (Enter to send)"
                rows={1}
                style={{ flex: 1, border: '1.5px solid var(--admin-border)', borderRadius: 10, padding: '9px 12px', fontSize: 13, fontFamily: 'inherit', resize: 'none', outline: 'none', lineHeight: 1.4, maxHeight: 100, minHeight: 38 }}
              />
              <button
                type="button"
                onClick={sendReply}
                disabled={!replyText.trim() || sending}
                style={{ width: 42, height: 42, borderRadius: 10, background: 'var(--admin-navy)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', alignSelf: 'flex-end', flexShrink: 0, opacity: !replyText.trim() ? 0.5 : 1 }}
              >
                <Send size={16} color="#fff" />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
